import React, { useState, useRef } from "react";
import { CloudUpload, Download, FileText, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ReactComponent as Eye } from "../../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../../icons/View_dlt.svg";
import ModHeader from "../../Mod/ModTable/ModHeader";
import { useSelector } from "react-redux";
import { ModRuvnlBulkUploadFile } from "../../../Utility/RestApi";
import Tooltip from "../../../../../components/tooltip";
import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
import MainHeader from "../../DateSession";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
// Helper function to format the date as DD-MMM-YYYY
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);
  return formattedDate.replace(/ /g, "-");
};

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
  isTemplate: boolean; // New field to track if it's a template file
};

const BaseFileRegexPatterns: { [key: string]: RegExp } = {
  "State Generating Unit": /^Entitlement(?!\(SGU\)).*\.(xlsx|csv)$/i,
  "NetSchdReportBreakup ISGS":
    /^NetSchdReportBreakup@RAJASTHAN_STATE@ISGS@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.(xlsx|csv)$/,
  "NetSchdReportBreakup OA_GNA":
    /^NetSchdReportBreakup@RAJASTHAN_STATE@OA_GNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.(xlsx|csv)$/,
  "NetSchdReportBreakup OA_REMC":
    /^NetSchdReportBreakup@RAJASTHAN_STATE@OA_REMC@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.(xlsx|csv)$/,
  "NetSchdReportBreakup OA_TGNA":
    /^NetSchdReportBreakup@RAJASTHAN_STATE@OA_TGNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.(xlsx|csv)$/,
  Intra_State_RE: /^Intra-RE.*\.xlsx$/i,
  Buffer: /^Buffer.*\.xlsx$/i,
};
const FileRegexPatterns: { [key: string]: RegExp } = {
  ...BaseFileRegexPatterns,

  State_Generating_Unit_TEMPLATE: /^Entitlement\(SGU\)\.xlsx$/i,
  ISGS_TEMPLATE: /^ISGS\.xlsx$/i,
  OA_TGNA_TEMPLATE: /^TGNA\.xlsx$/i,
  OA_GNA_TEMPLATE: /^GNA\.xlsx$/i,
  OA_REMC_TEMPLATE: /^REMC\.xlsx$/i,
};

const RegexToFileTypeMap: { [key: string]: string } = {
  // Original file patterns map to standard types
  "State Generating Unit": "State Generating Unit",
  "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
  "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
  "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
  "NetSchdReportBreakup OA_TGNA": "Net Schedule OA TGNA",
  Intra_State_RE: "Intra State RE",
  Buffer: "Buffer",

  // Template file patterns map to template types
  State_Generating_Unit_TEMPLATE: "State Generating Unit Template",
  ISGS_TEMPLATE: "Net Schedule ISGS Template",
  OA_GNA_TEMPLATE: "Net Schedule OA GNA Template",
  OA_REMC_TEMPLATE: "Net Schedule OA REMC Template",
  OA_TGNA_TEMPLATE: "Net Schedule OA TGNA Template",
};

// Template to Standard mapping
const TemplateToStandardMap: { [key: string]: string } = {
  "State Generating Unit Template": "State Generating Unit",
  "Net Schedule ISGS Template": "Net Schedule ISGS",
  "Net Schedule OA GNA Template": "Net Schedule OA GNA",
  "Net Schedule OA REMC Template": "Net Schedule OA REMC",
  "Net Schedule OA TGNA Template": "Net Schedule OA TGNA",
};

type FileRowProps = {
  fileName: string;
  uploadedFile: File | null;
  fileError: string | null;
  isTemplate: boolean;
  onFileChange: (
    fileName: string,
    file: File | null,
    isTemplate: boolean
  ) => void;
  onFileError: (fileName: string, error: string | null) => void;
};

// Helper function to get valid regex patterns for a specific file type (including template variants)
const getValidPatternsForFileType = (fileName: string): RegExp[] => {
  const patterns: RegExp[] = [];

  // Find direct patterns that map to this file type
  Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
    if (mappedType === fileName) {
      const pattern = FileRegexPatterns[regexKey];
      if (pattern) {
        patterns.push(pattern);
      }
    }
  });

  // Also check for template patterns
  const templateKey = Object.keys(TemplateToStandardMap).find(
    (key) => TemplateToStandardMap[key] === fileName
  );
  if (templateKey) {
    const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
      (key) => RegexToFileTypeMap[key] === templateKey
    );
    if (templateRegexKey && FileRegexPatterns[templateRegexKey]) {
      patterns.push(FileRegexPatterns[templateRegexKey]);
    }
  }

  return patterns;
};

const FileRow: React.FC<FileRowProps> = ({
  fileName,
  uploadedFile,
  fileError,
  isTemplate,
  onFileChange,
  onFileError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        fileExtension !== "xlsx" &&
        fileExtension !== "csv" &&
        fileExtension !== "pdf"
      ) {
        const errorMsg =
          "Invalid file type. Only .xlsx and .csv files are allowed.";
        // Show setError with file name
        setError(`Error uploading "${fileName}":\n${errorMsg}`);
        onFileError(fileName, errorMsg);
        onFileChange(fileName, null, false);
        return;
      }

      // For single file upload, validate against expected patterns for this specific file type
      const validPatterns = getValidPatternsForFileType(fileName);
      let detectedIsTemplate = false;

      if (validPatterns.length > 0) {
        const isValidFileName = validPatterns.some((pattern) =>
          pattern.test(file.name)
        );

        if (!isValidFileName) {
          const errorMsg = `File name does not match required pattern for ${fileName}.`;
          // Show setError with file name
          setError(`Error uploading "${fileName}":\n${errorMsg}`);
          onFileError(fileName, errorMsg);
          onFileChange(fileName, null, false);
          return;
        }

        // Check if the uploaded file matches a template pattern
        const templateKey = Object.keys(TemplateToStandardMap).find(
          (key) => TemplateToStandardMap[key] === fileName
        );
        if (templateKey) {
          const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
            (key) => RegexToFileTypeMap[key] === templateKey
          );
          if (
            templateRegexKey &&
            FileRegexPatterns[templateRegexKey] &&
            FileRegexPatterns[templateRegexKey].test(file.name)
          ) {
            detectedIsTemplate = true;
          }
        }
      }

      // If validation passes, upload the file with the expected fileName (not auto-detected type)
      onFileError(fileName, null);
      onFileChange(fileName, file, detectedIsTemplate);
    } else {
      onFileChange(fileName, null, false);
      onFileError(fileName, null);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
      // console.log(`Viewing file: ${uploadedFile.name}`);
    }
  };

  const handleDeleteFile = () => {
    onFileChange(fileName, null, false);
    onFileError(fileName, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isReady = uploadedFile && !fileError;
  const isError = !uploadedFile && fileError;

  // ✅ Config: manages all file -> columns mapping
  const fileConfig: Record<string, string[]> = {
    "State Generating Unit": [
      "Adanikawai",
      "BarsingsarNeyveli",
      "CREPLB_BIOMASS",
      "CSCTPP",
      "ChhabraTpsStage1Unit1234",
      "DholpurGtppUnit123",
      "GIRAL",
      "KTPS",
      "KalisindhTpsStage12",
      "MAHI",
      "RAPPA",
      "RPS",
      "Rajwest",
      "RGTPP",
      "SSCTPS_8",
      "STPS",
      "VSLIGNITE",
    ],
    "Net Schedule ISGS": [
      "ANTA_CRF",
      "ANTA_GF",
      "ANTA_LF",
      "ANTA_RF",
      "AURY_CRF",
      "AURY_GF",
      "AURY_LF",
      "AURY_RF",
      "BHAKRA",
      "BRBCL",
      "BSIUL",
      "CHAMERA1",
      "CHAMERA2",
      "CHAMERA3",
      "DADRI_CRF",
      "DADRI_GF",
      "DADRI_LF",
      "DADRI_RF",
      "DADRIT",
      "DADRT2",
      "DEHAR",
      "DHAULIGNGA",
      "DULHASTI",
      "JHAJJAR",
      "KHSTPP_I",
      "KHSTPP_II",
      "KISHANGANGA",
      "KOLDAM",
      "KOTESHWR",
      "NAPP",
      "NJPC",
      "PARBATI2",
      "PARBATI3",
      "PONG",
      "RAMPUR",
      "RAPPB",
      "RAPPC",
      "RAPPD",
      "RIHAND1",
      "RIHAND2",
      "RIHAND3",
      "SALAL",
      "SASAN",
      "SEWA2",
      "SINGRAULI",
      "SINGRAULI_HYDRO",
      "TALA",
      "TANAKPUR",
      "TANDA2",
      "TEHRI",
      "TEHRIPSP",
      "TPCL_Mundra",
      "UNCHAHAR1",
      "UNCHAHAR2",
      "UNCHAHAR3",
      "UNCHAHAR4",
      "URI",
      "URI2",
    ],

    "Net Schedule OA REMC": [
      "AAPL_BKN2",
      "AAPL_BKN2",
      "ACL_PSS3_KPS1_S",
      "ACL_PSS3_KPS1_S",
      "KARUR_JSWRETwo_W",
      "KOPPAL_AyanaSix_W",
      "SGEL_BKN2",
      "SGEL_BKN2",
      "SRI4PL_BKN2",
      "SRI5PL_BKN2",
      "SRI5PL_BKN2",
      "SRI5PL_BKN2",
      "SRI5PL_BKN2",
      "SRI5PL_BKN2",
      "Tuticorin_JSWRETwo",
    ],
    "Net Schedule OA GNA": [
      "DBPL",
      "KHURJA_STPP_UP1",
      "KWHEP",
      "MCCPL",
      "MEJA",
      "Teesta_III",
    ],
    "Net Schedule OA TGNA": ["add_generators"],
    "Intra State RE": ["SOLAR", "WIND", "BIOMASS", "SHEP"],
    Buffer: ["buffer"],
    // ✅ Special multi-row header file (hardcoded plant+sub-header structure)
    // "Net Schedule ISGS": [
    //   "BARH|TO_ODISHA",
    //   "BARH_I|TO_ODISHA",
    //   "CHUKHA|TO_ODISHA",
    //   "Darlipali_NTPC|TO_ODISHA",
    // ],
  };

  // ✅ File renaming logic
  function getCustomFileName(fileName: string) {
    switch (fileName) {
      case "State Generating Unit":
        return `Entitlement(SGU).xlsx`;
      case "Net Schedule ISGS":
        return `ISGS.xlsx`;
      case "Net Schedule OA REMC":
        return `REMC.xlsx`;
      case "Net Schedule OA GNA":
        return `GNA.xlsx`;
      case "Net Schedule OA TGNA":
        return `TGNA.xlsx`;
      case "Intra State RE":
        return `Intra-RE.xlsx`;
      case "Buffer":
        return `Buffer.xlsx`;
      default:
        return `${fileName}.xlsx`;
    }
  }

  // ✅ Main download logic
  const handleDownloadTemplate = (fileName: string) => {
    console.log(fileName);
    if (!fileName) return;

    const columns = fileConfig[fileName];
    if (!columns) {
      console.error("❌ Unknown fileName:", fileName);
      return;
    }

    const timeSlots = Array.from({ length: 96 }, (_, i) => i + 1);

    // 🔹 Special Case: Multi-row headers
    if (fileName === "") {
      const plants = columns.map((col) => {
        const [plant, sub] = col.split("|");
        return { name: plant, sub };
      });

      // Row 1 → plant names
      const headerRow = ["time_block", ...plants.map((p) => p.name)];
      // Row 2 → sub headers
      const subHeaderRow = ["", ...plants.map((p) => p.sub)];
      // Data rows
      const dataRows = timeSlots.map((slot) => [slot, ...plants.map(() => "")]);

      // Combine everything
      const worksheetData = [headerRow, subHeaderRow, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(blob, getCustomFileName(fileName));
      return;
    }

    // 🔹 Default Case (Single-row headers)
    const data = timeSlots.map((slot) => {
      const row: Record<string, any> = { time_block: slot };
      columns.forEach((col) => {
        row[col] = "";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, {
      header: ["time_block", ...columns],
    });
    XLSX.utils.sheet_add_aoa(worksheet, [["time_block", ...columns]], {
      origin: "A1",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, getCustomFileName(fileName));
  };

  return (
    <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
      <div className="flex items-center gap-3">
        <FileText size={20} style={{ color: "#8C8A96" }} />
        <span className="text-sm font-medium" style={{ color: "#525062" }}>
          {fileName}
        </span>
      </div>

      {isReady ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewFile}
              className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
            >
              <Eye />
            </button>
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#00BF6F", color: "#fff" }}
            >
              <CheckCircle size={14} /> <span>Ready</span>
            </button>

            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x-circle"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
              </svg>{" "}
              <span>Error</span>
            </button>
            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownloadTemplate(fileName)}
            className="flex items-center justify-center gap-2 w-[110px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors  bg-[#FFFAF6] text-[#F1935C]"
          >
            <Download className="w-4 h-4" />
            <span>Template</span>
          </button>
          <button
            onClick={handleUploadClick}
            className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
            style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
          >
            <CloudUpload size={14} /> Upload
          </button>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".xlsx,.csv,.pdf"
      />
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default function FileUploadTabComponent() {
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modRuvnlDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modRuvnlSession.modSession
  );
  const [loading, setLoading] = useState(false);
  const { navigateToPage } = useMeritEngineNavigation();

  // Standard file types (only these 12 will be displayed)
  const standardFileTypes = [
    "State Generating Unit",
    "Net Schedule ISGS",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "Net Schedule OA TGNA",
    "Intra State RE",
    "Buffer",
  ];

  const FileTypeBackendAlias: { [key: string]: string } = {
    // --- standard files ---
    "State Generating Unit": "generating-unit",
    "Net Schedule ISGS": "isgs",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "Net Schedule OA TGNA": "oa-tgna",
    "Intra State RE": "state-re",
    Buffer: "buffer",

    // --- template files ---
    "State Generating Unit Template": "generating-unit-temp",
    "Net Schedule ISGS Template": "isgs-temp",
    "Net Schedule OA REMC Template": "oa-remc-temp",
    "Net Schedule OA GNA Template": "oa-gna-temp",
    "Net Schedule OA TGNA Template": "oa-tgna-temp",
  };

  // Central state to store all uploaded files
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  // Central state to store all file errors
  const [fileErrors, setFileErrors] = useState<Record<string, string | null>>(
    {}
  );

  // Helper function to get the standard file name for a given file type
  const getStandardFileName = (fileType: string): string => {
    // If it's already a standard file type, return as is
    if (standardFileTypes.includes(fileType)) {
      return fileType;
    }
    // If it's a template file, map it to its standard counterpart
    return TemplateToStandardMap[fileType] || fileType;
  };

  // Helper function to check if a standard file type has an uploaded file (either standard or template)
  const getUploadedFileForStandardType = (
    standardFileName: string
  ): { file: File | null; isTemplate: boolean } => {
    // First, check if there's a standard file uploaded
    const standardFile = uploadedFiles.find(
      (f) => f.type === standardFileName && !f.isTemplate
    );
    if (standardFile) return { file: standardFile.file, isTemplate: false };

    // If no standard file, check for template file
    const templateFile = uploadedFiles.find((f) => {
      const standardName = getStandardFileName(f.type);
      return standardName === standardFileName && f.isTemplate;
    });
    if (templateFile) return { file: templateFile.file, isTemplate: true };

    return { file: null, isTemplate: false };
  };

  // Helper function to detect file type based on file name (used only for bulk upload)
  function detectFileType(fileName: string): {
    type: string;
    isTemplate: boolean;
  } {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
      if (pattern.test(fileName)) {
        const mappedType = RegexToFileTypeMap[regexKey];
        if (mappedType) {
          const isTemplate = mappedType.includes("Template");
          return { type: mappedType, isTemplate };
        }
      }
    }
    return { type: "", isTemplate: false }; // Return empty if no match found
  }

  const handleFileChange = (
    fileName: string,
    file: File | null,
    isTemplate: boolean
  ) => {
    if (!file) {
      // Remove the file
      setUploadedFiles((prevFiles) => {
        const standardFileName = getStandardFileName(fileName);

        return prevFiles.filter((f) => {
          const fStandardName = getStandardFileName(f.type);
          return fStandardName !== standardFileName;
        });
      });

      // Don't clear the error here if it's being set due to validation failure
      // Only clear error if it's a deliberate file removal (when error doesn't exist)
      setFileErrors((prevErrors) => {
        // If there's already an error, keep it (validation failure case)
        // If no error, clear it (deliberate removal case)
        if (!prevErrors[fileName]) {
          return {
            ...prevErrors,
            [fileName]: null,
          };
        }
        return prevErrors;
      });
      return;
    }

    // For single file upload, we use the fileName as provided (no auto-detection)
    // The validation is already done in FileRow component
    const standardFileName = getStandardFileName(fileName);

    setUploadedFiles((prevFiles) => {
      // Remove any previous files of this standard type (both standard and template variants)
      const filtered = prevFiles.filter((f) => {
        const fStandardName = getStandardFileName(f.type);
        return fStandardName !== standardFileName;
      });

      // Add new file with the fileName as provided (not auto-detected)
      return [
        ...filtered,
        {
          type: fileName, // Use the fileName as provided, not auto-detected
          date: preSelectedDate,
          file_url: URL.createObjectURL(file),
          file,
          id: Date.now(),
          isTemplate: isTemplate, // Track if it's a template file
        },
      ];
    });

    // Clear error when a valid file is successfully uploaded
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: null,
    }));
  };

  const handleFileError = (fileName: string, error: string | null) => {
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: error,
    }));
  };

  const [selectedFileType, setSelectedFileType] = useState<string>("All Files");

  // Logic to filter the list of files based on dropdown selection
  const filesToDisplay =
    selectedFileType === "All Files"
      ? standardFileTypes
      : standardFileTypes.filter((fileType) => fileType === selectedFileType);

  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const handleBulkUploadClick = () => {
    bulkFileInputRef.current?.click();
  };

  function handleBulkUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!preSelectedDate) {
      setError("Please select a date");
      event.target.value = "";
      return;
    }

    if (!files || files.length === 0) {
      setError("Please select files");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".csv", ".pdf"];
    const newFiles: UploadedFile[] = [];
    const undetectedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError(
          `Invalid file format for ${file.name}. Please select only XLSX, XLS or PDF files.`
        );
        event.target.value = "";
        return;
      }

      // For bulk upload, we use auto-detection
      const { type: detectedType, isTemplate } = detectFileType(file.name);

      if (!detectedType) {
        undetectedFiles.push(file.name);
        continue;
      }

      newFiles.push({
        type: detectedType, // Use the detected type (which could be template type)
        date: preSelectedDate,
        file_url: URL.createObjectURL(file),
        file,
        id: Date.now() + i, // ensure uniqueness
        isTemplate: isTemplate, // Track if it's detected as template
      });
    }

    if (undetectedFiles.length > 0) {
      setError(
        `Could not auto-detect file type for:\n${undetectedFiles.join(
          "\n"
        )}\n\nPlease upload these files individually.`
      );
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => {
        // For bulk upload, we need to handle both standard and template files
        const affectedStandardTypes = new Set<string>();
        newFiles.forEach((file) => {
          const standardName = getStandardFileName(file.type);
          affectedStandardTypes.add(standardName);
        });

        // Revoke object URLs for replaced types to avoid memory leaks
        prev.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          if (affectedStandardTypes.has(standardName)) {
            URL.revokeObjectURL(f.file_url);
          }
        });

        // Keep old files whose standard types are NOT affected by this upload
        const kept = prev.filter((f) => {
          const standardName = getStandardFileName(f.type);
          return !affectedStandardTypes.has(standardName);
        });

        return [...kept, ...newFiles];
      });

      // Clear errors for the (re)uploaded types
      setFileErrors((prev) => {
        const updated = { ...prev };
        newFiles.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          updated[standardName] = null;
        });
        return updated;
      });
    }

    event.target.value = "";
  }

  const GotoModTable = async () => {
    // Check if we have files for all 12 standard types
    const uploadedStandardTypes = standardFileTypes.filter((standardType) => {
      const { file } = getUploadedFileForStandardType(standardType);
      return file !== null;
    });

    if (uploadedStandardTypes.length !== 7) {
      setError(
        `Please upload files for all 7 file types. Currently uploaded: ${uploadedStandardTypes.length}/7`
      );
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      // Build metadata and append files for all uploaded files
      // console.log("Uploaded files:", uploadedFiles);
      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);

        // Determine the correct backend type based on whether it's a template
        let backendType: string;
        if (file.isTemplate) {
          // For template files, use base backend alias + "-temp"
          const standardName = getStandardFileName(file.type);
          const baseAlias = FileTypeBackendAlias[standardName];
          backendType = baseAlias ? `${baseAlias}-temp` : file.type;
        } else {
          // For standard files, use standard backend alias
          backendType = FileTypeBackendAlias[file.type] || file.type;
        }

        return {
          field, // reference to the file field
          date: file.date,
          type: backendType,
        };
      });

      // console.log("Metadata being sent:", metaData);

      // Attach metadata as a single JSON string
      formData.append("metaData", JSON.stringify(metaData));
      formData.append("date", preSelectedDate);
      formData.append("session", modSelectedSession);

      const res = await ModRuvnlBulkUploadFile(formData);
      setUploadedFiles([]);
      setSelectedFileType("All Files");

      if (res) {
        navigateToPage("merit-order-dispatch");
        (window as any).resetNextTabs?.("GENERATION");
      }
    } catch (error) {
      console.error("Error generating file:", error);
      setError("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Count how many standard file types have uploaded files
  const uploadedCount = standardFileTypes.filter((standardType) => {
    const { file } = getUploadedFileForStandardType(standardType);
    return file !== null;
  }).length;

  return (
    <div className="flex flex-col w-full">
      <MainHeader />
      <div className="h-[calc(-73px+100vh)] p-4">
        <div className="w-full h-full flex flex-col gap-4">
          <ModHeader title="Available Generation Capacity" />
          <div className="gas-chart-container ">
            <div className="gas-heading gap-x-10">
              <div className="flex gap-x-6 w-full justify-between">
                <div className="flex gap-x-4">
                  <select
                    className="border border-[#C5C5CBD9] rounded-lg p-2 max-w-[200px] text-[#8C8A96]"
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                  >
                    <option value="All Files">All Files</option>
                    {standardFileTypes.map((fileType, index) => (
                      <option key={index} value={fileType}>
                        {fileType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-x-4">
                  <button
                    type="submit"
                    disabled={uploadedCount !== 7 || loading}
                    onClick={GotoModTable}
                    className="flex items-center gap-2 px-3 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                  >
                    {loading ? "Processing..." : `Verify & Generate MOD`}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="p-4 mt-3"
              style={{
                backgroundColor: "#F1F5F980",
                border: "1px solid #CAD5E2",
                borderRadius: "15px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3
                    className="text-xl font-medium"
                    style={{ color: "#6F6D7C" }}
                  >
                    Add Files
                  </h3>
                  <p className="text-large" style={{ color: "#8C8A96" }}>
                    Attach all the mentioned files below, either individual
                    files or all the files together, to generate the MOD Table.
                    You can upload either standard files or template files.
                  </p>
                </div>
                <button
                  onClick={handleBulkUploadClick}
                  className="flex items-center justify-center gap-2 w-[180px] h-[37px] text-base rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                >
                  <CloudUpload size={20} /> Bulk Upload
                </button>
              </div>
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkUpload}
                className="hidden"
                multiple
                accept=".xlsx,.xls,.csv,.pdf"
              />
            </div>
            <h2
              className="mx-4 py-2 chartHeading border-b !text-lg !font-medium"
              style={{ color: "#6F6D7C" }}
            >
              File Types
            </h2>

            <div className="px-4 h-100 overflow-y-auto">
              {filesToDisplay.map((fileName) => {
                const { file: uploadedFile, isTemplate } =
                  getUploadedFileForStandardType(fileName);
                return (
                  <FileRow
                    key={fileName}
                    fileName={fileName}
                    uploadedFile={uploadedFile}
                    isTemplate={isTemplate}
                    fileError={fileErrors[fileName] || null}
                    onFileChange={handleFileChange}
                    onFileError={handleFileError}
                  />
                );
              })}
            </div>
          </div>
          <ErrorPopup error={error} onClose={() => setError(null)} />
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <p>
                    Default values and variable charges auto-fetched from
                    system.
                  </p>
                }
                text=" "
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
